<template>
  <b-modal
    ref="modal"
    :visible="show"
    :title="title"
    :content-class="contentClass"
    :body-class="bodyClass"
    :dialog-class="modalDialogClass"
    header-border-variant="0"
    :size="size"
    hide-footer
    :hide-header="hideHeader"
    :scrollable="scrollable"
    :ignore-enforce-focus-selector="enforceFocusOn"
    :static="loadInPlace"
    :lazy="loadInPlace"
    @shown="$emit('shown')"
    @hidden="$emit('hidden')"
    @hide="onModalHide"
    :no-close-on-backdrop="noCloseOnBackdrop"
    :no-close-on-esc="noCloseOnEsc"
    :modal-class="{
      minimized: visibilityState === VISIBILITY_STATE.MINIMIZED,
      stacked: visibilityState === VISIBILITY_STATE.STACKED
    }"
    :header-class="headerClass"
  >
    <div class="w-100" slot="modal-header">
      <h5 class="modal-title">
        <!-- @slot Slot for Modal Heading/Title -->
        <slot name="modal-title" v-if="$slots['modal-title']"></slot>
        <template v-else-if="title">{{ title }}</template>
      </h5>
      <div class="modal-top-actions d-flex">
        <span
          :id="'minimize-' + id"
          class="minimize rounded-icon flex-center mr-2 text-big"
          title="minimize"
          @click="onMinimizeClick"
          v-if="minimizationConfig"
          >-</span
        >
        <i
          class="close-rounded-icon"
          @keydown.enter="$refs.modal.hide()"
          @click="$refs.modal.hide()"
          title="close"
          tabindex="0"
        ></i>
      </div>
    </div>
    <div class="modal-top-actions d-flex" v-if="showCloseIcon">
      <i class="close-rounded-icon" @click="$refs.modal.hide()" tabindex="0"></i>
    </div>
    <!-- @slot Default slot for modal content -->
    <slot></slot>
    <div class="d-flex custom-footer" v-if="!hideFooter">
      <submit-buttons
        class="flex-grow-1"
        :is-submit-disabled="isSubmitDisabled"
        :submit-text="submitButtonText"
        :cancel-text="cancelButtonText"
        :is-loading="isSubmitting"
        :variant="submitBtnVariant"
        @save="$emit('submit')"
        @cancel="hide"
      >
        <slot name="footer-right"></slot>
      </submit-buttons>
    </div>
  </b-modal>
</template>

<script>
import events from '@/constants/events'
import { VISIBILITY_STATE } from '@/constants/minimize'
import { STORAGE_KEYS } from '@/constants/storage'
import EventBusMixin from '@/mixins/EventBusMixin'
import { BModal } from 'bootstrap-vue'
import SubmitButtons from './SubmitButtons'

/**
 * Props:
 *    - title: Text to be shown as header
 *    - submitButtonText: This defaults to "Done"
 *    - cancelButtonText: This defaults to "Cancel"
 *    - modelClass: Use this, if you want to add some custom class to modal-content
 *    - hideFooter: Use this to hide footer (submit bar)
 * Emits:
 @submit: Emitted when submit button is clicked
 * Slots
 * - minimizedView: view(html) that needs to be rendered in minimized-bar when modal is minimized
 */

/**
 * This is a modal component. Use v-model to control its visibility
 */
export default {
  name: 'Modal',
  model: {
    prop: 'show',
    event: 'hidden'
  },
  props: {
    /**
     * show the modal
     */
    show: Boolean,
    /**
     * heading of the modal
     */
    title: String,
    /**
     * Text of submit button
     */
    submitButtonText: {
      default: 'Done'
    },
    /**
     * Text of submit button
     */
    isSubmitDisabled: {
      default: false
    },
    /**
     * Text of cancel button
     */
    cancelButtonText: {
      default: 'Cancel'
    },
    /**
     * Use this, if you want to add some custom class to modal-content
     */
    modalClass: {
      default: ''
    },
    /**
     * Use this to hide footer (submit bar)
     */
    hideFooter: { default: false },
    /**
     * Use this to hide header
     */
    hideHeader: { default: false },
    size: { default: 'md' },
    /*
      BModal does not allow focus on outside elements when it is visible.
      There can be cases when it is required, like when we have ckeditor in modal and link modal is opened..
      Since Link modal is rendered outside BModal, by default BModal will prevent focus on input fields within Link modal.
      css selector (as enforceFocusOn) can be provided to allow focus on such fields
       */
    enforceFocusOn: { default: '.cke_dialog_ui_input_text, .copy-to-clipboard-textarea' }, // default is for handling modal opening from ckeditor
    /* class to add to modal-body */
    bodyClass: { default: '' },
    /* This tell if modal needs to be rendered in parent node, else it will render in body */
    loadInPlace: { default: false },
    noCloseOnBackdrop: { default: false },
    showCloseIcon: { default: false },
    /* this contain the config if modal is minimizable */
    minimizationConfig: Object,
    /* this contain id and state(MIMIZED/MAXIMIZED) of modal */
    modalData: { default: () => ({}) },
    scrollable: {
      default: true
    },
    isSubmitting: Boolean,
    noCloseOnEsc: Boolean,
    headerClass: String,
    submitBtnVariant: String
  },
  components: {
    BModal,
    SubmitButtons
  },
  mixins: [EventBusMixin],
  data() {
    return {
      VISIBILITY_STATE,
      STORAGE_KEYS
    }
  },
  computed: {
    modalDialogClass() {
      return 'stack-order-' + this.modalData.stackOrder + ' ' + this.modalClass
    },
    visibilityState() {
      return this.modalData?.state || VISIBILITY_STATE.MAXIMIZED
    },
    contentClass() {
      return 'text-medium-1 custom-modal ' + (this.hideFooter ? ' footer-hidden' : '')
    },
    id() {
      return this.modalData?.id
    }
  },
  methods: {
    hide() {
      this.$refs.modal.hide()
    },
    minimize() {
      this.eventBusEmit(events.MODAL_MINIMIZE, {
        ...this.minimizationConfig,
        _id: this.id,
        view: this.$slots.minimizedView
      })
    },
    onModalHide(ev) {
      if (
        ['esc', 'backdrop'].includes(ev.trigger) &&
        this.previousVisibility === VISIBILITY_STATE.MINIMIZED
      ) {
        ev.preventDefault()
        this.minimize()
      }
      this.$emit('hide')
    },
    onMinimizeClick() {
      this.minimize()
      this.$refs.dockTooltip?.dismissTip()
    }
  },
  watch: {
    visibilityState(current, previous) {
      this.previousVisibility = previous
      if (current === VISIBILITY_STATE.MAXIMIZED && previous === VISIBILITY_STATE.MINIMIZED) {
        this.$refs.modal.focusFirst()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/forms';
@import '~@/styles/_styleConfigurations.scss';

/deep/ .modal-header {
  padding: 30px 30px 10px 30px;
}

/deep/ .modal-body {
  padding: 20px 30px 0 30px;
}

/deep/ .footer-hidden {
  padding-bottom: 20px;
}

.custom-footer {
  position: sticky;
  bottom: 0;
  background: $documentColor;
  padding-top: 20px;
  padding-bottom: 20px;
  z-index: 2;
}

.modal-top-actions {
  position: absolute;
  right: 18px;
  top: 25px;
  z-index: 1;
}

@include media-breakpoint-down(md) {
  /deep/ .modal-header {
    padding: 15px 15px 10px 15px;
  }

  /deep/ .modal-body {
    padding: 20px 15px 0 15px;
  }
}

@include media-breakpoint-up(sm) {
  /deep/ .modal-dialog.modal-xl {
    margin: 20px auto;
  }
}
</style>

<style lang="scss">
.modal.minimized {
  &,
  + .modal-backdrop {
    display: none !important;
  }
}

.modal.stacked {
  transform: scaleX(0.95);
}

.modal {
  transition: transform ease-out 200ms;
  transform: translate3d(0, 0, 0);
}

.stack-order-0 {
  margin-top: 20px !important;
}

.stack-order-1 {
  margin-top: 40px !important;
}
</style>
